import {
  apiFetch,
  apiJson,
  refreshAccessToken,
  ApiError,
} from '@/lib/apiClient';
import { setAccessToken, clearAccessToken } from './tokenStore';

export type Role = 'owner' | 'manager' | 'viewer';

export interface User {
  id: string;
  name: string;
  /**
   * The signed-in person's phone — a string for a real tenant session.
   *
   * 🔴 `null` UNDER IMPERSONATION (slice 17c). A read-only impersonation session
   * is a Raad staff member looking at a customer; there is NO user, so the body
   * carries no person phone (`GET /api/me` emits `phone: null`, see the server's
   * impersonation branch). Every reader must treat this as nullable — render a
   * dash or nothing, never assume a string.
   */
  phone: string | null;
  role: Role;
  tenantId: string;
  language: 'en' | 'so';
  /**
   * WHICH kind of session carries this body. `'tenant'` is a real customer user;
   * `'impersonation'` is a Raad staff member viewing that tenant READ-ONLY (slice
   * 17c). Both render the tenant app against `tenantId`; impersonation additionally
   * hides every write affordance (see `readOnly` and `useTenantWritable`).
   */
  principal: 'tenant' | 'impersonation';
  /**
   * `true` ONLY for a read-only impersonation session (slice 17c). The server also
   * enforces read-only independently (`authenticate` refuses any non-GET with
   * `read_only_session`), so this is the client's affordance-hiding half, not the
   * security boundary. Absent / `false` for a real tenant session.
   */
  readOnly?: boolean;
  /**
   * The tenant's IANA time zone (e.g. `Africa/Mogadishu`), from `GET /api/me`.
   * SEAM: the backend half of this contract is in flight. Optional here only
   * because the field may not be on the wire yet — when it is absent,
   * `useTenantTimeZone` falls back to UTC (an honest, machine-independent
   * default), NEVER to the browser's zone. It is not softened to a hardcoded
   * `Africa/Mogadishu`, which would render a Zambian tenant's days in the wrong
   * zone while looking correct in Mogadishu.
   */
  timezone?: string;
  /**
   * What this tenant pays per month, in INTEGER CENTS USD, from `GET /api/me`
   * (seam S7, slice 15b). Cents on the wire; the client formats.
   *
   * 🔴 `null` MEANS NO RATE RECORDED, AND MUST NOT BE COERCED TO A NUMBER.
   * The column is nullable with no default precisely so an unset rate stays
   * VISIBLY unset — the trap `tenants.plan` fell into with `DEFAULT 'track'`,
   * where a defaulted value is indistinguishable from a chosen one. Rendering
   * `0` here would claim the customer pays nothing; falling back to a list price
   * would invent a rate for a tenant who may be discounted or free. The Plan
   * tile renders its no-claim state instead.
   *
   * Optional (`?`) covers the wire not carrying the key at all — the same
   * absent-vs-null distinction `timezone` documents above; both collapse to
   * "no rate to show".
   */
  monthlyRateCents?: number | null;
  /**
   * The TENANT's own display name (`tenants.name`), from `GET /api/me` (seam
   * S-2, slice 16). Distinct from `name`, which is the signed-in PERSON — they
   * happen to match for a single-user tenant, which is exactly why the Settings
   * screen must read this field and never substitute the other.
   *
   * Optional for the same reason as `timezone`: the key may not be on the wire
   * from an older API. Absent renders as "not recorded", never as the user's
   * own name.
   */
  tenantName?: string;
  /**
   * The capabilities this tenant may render a control for (slice 14, `/api/me`).
   *
   * 🔴 A CAPABILITY THE TENANT DOES NOT HAVE IS ABSENT FROM THIS LIST, NEVER
   * PRESENT-AND-FALSE — the wire shape of "absence over disabled". So the client
   * asks "is `raad_secure` in the list?", never "is the flag false?". A body from
   * an older API carries no `capabilities` key at all; `capabilitiesOf` softens
   * that absent case to `[]` (no capability), never to a fabricated one.
   */
  capabilities?: string[];
  /**
   * Whether this user has a command PIN set (slice 14c, `/api/me`). The PIN value
   * itself never leaves the database — this is only WHETHER one exists, so the
   * client can send the user to set one instead of prompting for a PIN they have
   * never chosen. Absent on the wire → treated as `false` (no PIN).
   */
  hasCommandPin?: boolean;
  /**
   * Whether this tenant may WRITE (slice 11b, `/api/me`). `false` while the tenant
   * is SUSPENDED (unpaid): reads still succeed — a suspended customer sees their
   * map, vehicles, trips, reports and alerts normally — but every write is refused
   * with a 402, and the UI HIDES the write affordances (absence over disabled) and
   * shows a persistent pay-to-restore banner.
   *
   * 🔴 ABSENT MEANS WRITABLE. An older API body carries no `writable` key; a
   * missing field is treated as `true` so existing tenants keep working while the
   * backend half of the seam lands. Only an explicit `writable === false` gates.
   * Read it through `useTenantWritable()`, never off this field directly, so the
   * absent-vs-false rule lives in one place.
   */
  writable?: boolean;
}

/**
 * The capability list off a `/api/me` user, softened to `[]` when the key is
 * absent (an older API) or the value is not an array. One place decides how an
 * absent list reads, so no caller has to remember the absent-vs-empty rule.
 */
export function capabilitiesOf(user: Pick<User, 'capabilities'> | null | undefined): string[] {
  return Array.isArray(user?.capabilities) ? user!.capabilities : [];
}

export function hasCapability(
  user: Pick<User, 'capabilities'> | null | undefined,
  capability: string,
): boolean {
  return capabilitiesOf(user).includes(capability);
}

/**
 * Boundary guard for a `/api/me` body (board open item 2 — `apiJson` CASTS
 * rather than verifies, and a well-formed body from a DIFFERENT endpoint took
 * the whole Overview down during slice 15).
 *
 * This one is load-bearing beyond the usual: `adoptUserLanguage` reads
 * `language` off whatever this resolves to and applies it to the entire app. A
 * body without it would leave the user on the wrong language with no error
 * anywhere — the slice-16 defect wearing a different hat.
 */
export function isUser(value: unknown): value is User {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.phone === 'string' &&
    (v.language === 'en' || v.language === 'so') &&
    v.principal === 'tenant'
  );
}

/**
 * A READ-ONLY IMPERSONATION body (slice 17c) — a THIRD principal shape the tenant
 * client must recognise. Admin's "Open portal" mints a token with `pt:'tenant'`,
 * `ro:true` and the staff id as `sub`; `GET /api/me` answers a tenant-shaped body
 * whose `principal` is `'impersonation'`, `phone` is `null` (there is no user —
 * see the server's impersonation branch in `api/src/modules/auth/routes.ts`), and
 * `readOnly` is `true`.
 *
 * 🔴 THIS IS A POSITIVE CHECK, NEVER "not staff". It requires BOTH
 * `principal === 'impersonation'` AND `readOnly === true`, because the banner and
 * the write-affordance hiding both key off read-only — a body claiming the
 * principal without the flag must not be promoted into a session that then looks
 * writable. `phone` is string-or-null here (null in practice), deliberately
 * distinct from `isUser`'s string-only tenant check, which is EXACTLY why the two
 * are separate predicates rather than one loosened one. Before this existed,
 * `isUser` rejected the body and `getMe` threw `malformed_profile`, so the portal
 * silently dropped to the login screen (the 7 Oct defect).
 */
export function isImpersonationUser(value: unknown): value is User {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    (typeof v.phone === 'string' || v.phone === null) &&
    (v.language === 'en' || v.language === 'so') &&
    typeof v.tenantId === 'string' &&
    v.principal === 'impersonation' &&
    v.readOnly === true
  );
}

/**
 * A STAFF principal reaching the tenant door. `/api/me` returns
 * `principal: 'staff'` for a Raad staff account, and the single shared
 * `POST /auth/login` resolves staff too — so a staff account can sign in on the
 * customer origin. The tenant app has no fleet for them; casting the body to a
 * `User` boots "authenticated" and then 403s on every tenant call (the observed
 * symptom, and the board's standing "apiJson CASTS rather than verifies" item on
 * the auth path). We DETECT it instead and let the caller render the in-place
 * panel. This is a positive check for `staff`, never "not a tenant" — a
 * malformed body is neither and must throw, not fall into the staff branch.
 */
export function isStaffPrincipal(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  return (value as Record<string, unknown>).principal === 'staff';
}

/** The two principals `/api/me` can legitimately resolve to for this client. */
export type MeResult = { kind: 'tenant'; user: User } | { kind: 'staff' };

interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  // NOTE (E1 = B-cookie): the refresh token is NOT in the body — raad-api sets
  // it as an httpOnly cookie. If it ever reappears here, that is a backend
  // regression, not something this client should read or store.
}

export class InvalidCredentialsError extends Error {
  constructor() {
    super('invalid_credentials');
    this.name = 'InvalidCredentialsError';
  }
}

/**
 * GET the signed-in principal — VERIFIED, not cast. A valid tenant body resolves
 * to the user; a staff body resolves to the staff branch (the caller renders the
 * panel, never the fleet); anything else throws loudly, because a malformed body
 * must not be silently promoted into either branch. 401 here transparently
 * triggers single-flight refresh inside `apiFetch`.
 */
export async function getMe(): Promise<MeResult> {
  const body = await apiJson<unknown>('/api/me');
  if (isUser(body)) return { kind: 'tenant', user: body };
  // A read-only impersonation session renders the tenant app against the
  // customer's `tenantId`, so it resolves to a `tenant` MeResult — but its `user`
  // carries `principal: 'impersonation'` and `readOnly: true`, which the banner
  // and `useTenantWritable` read to show whose data it is and to hide every write.
  if (isImpersonationUser(body)) return { kind: 'tenant', user: body };
  if (isStaffPrincipal(body)) return { kind: 'staff' };
  throw new ApiError(200, 'malformed_profile');
}

/**
 * Phone + password sign-in. A 401 is `invalid_credentials` (byte-identical for
 * wrong password and unknown phone by design) — NOT a refresh trigger, so we
 * disable the auth-retry. On success the access token goes to memory and we
 * immediately resolve the user.
 */
export async function login(phone: string, password: string): Promise<MeResult> {
  const res = await apiFetch(
    '/auth/login',
    // SEAM A (17i): `audience: 'tenant'` names which door this login came from.
    // The server treats it as OPTIONAL and falls back to today's staff-first
    // resolution when absent, so this cannot break a stale cached bundle. Owned
    // jointly with raad-backend; the values are exactly 'tenant' | 'staff'.
    { method: 'POST', body: JSON.stringify({ phone, password, audience: 'tenant' }) },
    { retryOnAuth: false },
  );
  if (res.status === 401) throw new InvalidCredentialsError();
  if (!res.ok) throw new ApiError(res.status, null);

  const data = (await res.json()) as LoginResponse;
  if (!data.accessToken) throw new ApiError(res.status, 'no_token');
  setAccessToken(data.accessToken);
  return getMe();
}

/**
 * PATCH the signed-in user's own profile (slice 16, B-1). Absent keys are left
 * untouched server-side, so a language change never disturbs the name.
 *
 * Returns the FULL refreshed user — the same shape `GET /api/me` returns, by
 * construction on the server (seam S-1). The caller feeds it straight back into
 * the auth state so `adoptUserLanguage` sees the new value rather than the one
 * it cached at boot.
 */
export async function updateProfile(patch: {
  name?: string;
  language?: 'en' | 'so';
}): Promise<User> {
  const body = await apiJson<unknown>('/api/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  // Verified, not cast. If the server ever stops returning the profile here,
  // this throws loudly instead of handing a language-less object to the code
  // that sets the app's language.
  if (!isUser(body)) throw new ApiError(200, 'malformed_profile');
  return body;
}

export class PasswordRejectedError extends Error {
  constructor(readonly reason: 'invalid_credentials' | 'password_unchanged' | 'too_short') {
    super(reason);
    this.name = 'PasswordRejectedError';
  }
}

/**
 * Change the signed-in user's password (slice 16, B-2).
 *
 * 🔴 A 204 HERE MEANS THE SESSION IS GONE. The server revokes every refresh
 * token this user holds — including this browser's — so the caller MUST sign
 * out locally rather than carry on with an access token that cannot be
 * refreshed. Silently continuing would leave the user working until the access
 * token expires and then dropping them at a random moment.
 *
 * `retryOnAuth: false`: a 401 here is "your current password is wrong", not an
 * expired access token, and must not trigger a refresh round-trip.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await apiFetch(
    '/api/me/password',
    { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) },
    { retryOnAuth: false },
  );
  if (res.status === 401) throw new PasswordRejectedError('invalid_credentials');
  if (res.status === 400) {
    let reason: 'password_unchanged' | 'too_short' = 'too_short';
    try {
      const body = (await res.json()) as { error?: unknown };
      if (body?.error === 'password_unchanged') reason = 'password_unchanged';
    } catch {
      /* a 400 with no readable body is treated as the length rejection */
    }
    throw new PasswordRejectedError(reason);
  }
  if (!res.ok) throw new ApiError(res.status, null);
}

/**
 * The command PIN (slice 14c). Set/change and remove both require the login
 * PASSWORD — a stolen unlocked phone must not be able to grant itself the very
 * capability the PIN exists to withhold.
 *
 * 🔴 THE PIN IS A STRING END TO END. It is never coerced to a number anywhere on
 * this path, because `0421` would become `421` and then fail to verify for a
 * reason no user or log line can see. The server validates `min(4).max(32)`.
 */
export class CommandPinRejectedError extends Error {
  constructor(readonly reason: 'invalid_credentials' | 'too_short') {
    super(reason);
    this.name = 'CommandPinRejectedError';
  }
}

/** POST /api/me/command-pin — 204 on success, 401 on a wrong login password. */
export async function setCommandPin(currentPassword: string, pin: string): Promise<void> {
  const res = await apiFetch(
    '/api/me/command-pin',
    // `retryOnAuth:false` — a 401 here is "your login password is wrong", NOT an
    // expired access token, so it must not trigger a refresh round-trip.
    { method: 'POST', body: JSON.stringify({ currentPassword, pin }) },
    { retryOnAuth: false },
  );
  if (res.status === 401) throw new CommandPinRejectedError('invalid_credentials');
  // A 400 is the zod floor (pin shorter than 4). The client already guards this,
  // so it should be unreachable — surfaced honestly rather than as a generic fail.
  if (res.status === 400) throw new CommandPinRejectedError('too_short');
  if (!res.ok) throw new ApiError(res.status, null);
}

/** DELETE /api/me/command-pin — 204 on success, 401 on a wrong login password. */
export async function removeCommandPin(currentPassword: string): Promise<void> {
  const res = await apiFetch(
    '/api/me/command-pin',
    { method: 'DELETE', body: JSON.stringify({ currentPassword }) },
    { retryOnAuth: false },
  );
  if (res.status === 401) throw new CommandPinRejectedError('invalid_credentials');
  if (!res.ok) throw new ApiError(res.status, null);
}

/** Best-effort logout: tell raad-api to revoke + clear the cookie, then wipe local state. */
export async function logout(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { method: 'POST', body: '{}' }, { retryOnAuth: false });
  } catch {
    /* a failed network logout still clears local state below */
  }
  clearAccessToken();
}

/**
 * Silent session restore on app boot. The httpOnly refresh cookie (if present)
 * mints an access token; then we load the user. Any failure means "not signed
 * in" — return null and show login, never an error.
 */
export async function bootstrap(): Promise<MeResult | null> {
  try {
    await refreshAccessToken();
  } catch {
    return null;
  }
  try {
    return await getMe();
  } catch {
    clearAccessToken();
    return null;
  }
}
